import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import { getPublicSiteFs, savePublicSiteFs } from '../../utils/firestoreStore';
import { homepageDefaults } from '../../publicSite/homepageDefaults';

const mergeHomepageContent = (source = {}) => ({
  ...homepageDefaults,
  ...source,
  hero: { ...homepageDefaults.hero, ...(source.hero || {}) },
  heroSlides: source.heroSlides?.length ? source.heroSlides : homepageDefaults.heroSlides,
  courses: {
    ...homepageDefaults.courses,
    ...(source.courses || {}),
    cards: source.courses?.cards?.length ? source.courses.cards : homepageDefaults.courses.cards
  },
  video: {
    ...homepageDefaults.video,
    ...(source.video || {}),
    heading: source.video?.heading === 'Course Overview Video'
      ? 'Video of the Week'
      : (source.video?.heading || homepageDefaults.video.heading)
  },
  whyChoose: {
    ...homepageDefaults.whyChoose,
    ...(source.whyChoose || {}),
    cards: source.whyChoose?.cards?.length ? source.whyChoose.cards : homepageDefaults.whyChoose.cards
  },
  events: { ...homepageDefaults.events, ...(source.events || {}) },
  cta: { ...homepageDefaults.cta, ...(source.cta || {}) }
});

export default function AdminHomepage() {
  const useFirebase = process.env.REACT_APP_USE_FIREBASE === 'true';
  const [homepageContent, setHomepageContent] = useState(homepageDefaults);
  const [status, setStatus] = useState('');
  const [statusTone, setStatusTone] = useState('info');
  const [uploadingKey, setUploadingKey] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [showSavedModal, setShowSavedModal] = useState(false);
  const fileInputRef = useRef(null);
  const pendingUploadRef = useRef(null);

  useEffect(() => {
    if (!useFirebase) return;
    getPublicSiteFs()
      .then((data) => {
        if (data?.homepageContent) {
          setHomepageContent(mergeHomepageContent(data.homepageContent));
        }
      })
      .catch(() => {});
  }, [useFirebase]);

  const imageTargets = useMemo(() => ({
    heroSlides: homepageContent.heroSlides || [],
    courseCards: homepageContent.courses.cards || []
  }), [homepageContent]);

  const updateField = (section, field, value) => {
    setHomepageContent((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateCard = (section, index, field, value) => {
    setHomepageContent((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        cards: prev[section].cards.map((card, cardIndex) =>
          cardIndex === index ? { ...card, [field]: value } : card
        )
      }
    }));
  };

  const updateHeroSlide = (index, field, value) => {
    setHomepageContent((prev) => ({
      ...prev,
      heroSlides: prev.heroSlides.map((slide, slideIndex) =>
        slideIndex === index ? { ...slide, [field]: value } : slide
      )
    }));
  };

  const startImageUpload = (target) => {
    setStatus('');
    pendingUploadRef.current = target;
    fileInputRef.current?.click();
  };

  const handleImageFileChange = async (event) => {
    const file = event.target.files?.[0];
    const target = pendingUploadRef.current;
    event.target.value = '';
    if (!file || !target || !useFirebase) return;

    setUploadingKey(target.key);
    setUploadProgress(0);
    setStatus('Uploading image...');
    setStatusTone('info');
    try {
      const storagePath = `public/homepage/${Date.now()}-${file.name}`;
      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on('state_changed', (snapshot) => {
        const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setUploadProgress(progress);
      });

      await uploadTask;
      const downloadUrl = await getDownloadURL(storageRef);

      if (target.kind === 'heroSlide') {
        updateHeroSlide(target.index, 'src', downloadUrl);
      } else if (target.kind === 'courseCard') {
        setHomepageContent((prev) => ({
          ...prev,
          courses: {
            ...prev.courses,
            cards: prev.courses.cards.map((card, cardIndex) =>
              cardIndex === target.index ? { ...card, image: downloadUrl } : card
            )
          }
        }));
      }

      setStatus('Image uploaded. Save homepage sections to publish it.');
      setStatusTone('success');
      setTimeout(() => setStatus(''), 3000);
    } catch (error) {
      console.error('Failed to upload homepage image', error);
      setStatus('Unable to upload image.');
      setStatusTone('danger');
      setTimeout(() => setStatus(''), 3000);
    } finally {
      setUploadingKey('');
      setUploadProgress(0);
      pendingUploadRef.current = null;
    }
  };

  const handleSave = async () => {
    if (!useFirebase) {
      setStatus('Firebase is disabled, so homepage changes cannot be saved.');
      setStatusTone('danger');
      setTimeout(() => setStatus(''), 3000);
      return;
    }
    setIsSaving(true);
    setStatus('Saving homepage sections...');
    setStatusTone('info');
    try {
      await savePublicSiteFs({ homepageContent });
      setStatus('Homepage sections updated.');
      setStatusTone('success');
      setShowSavedModal(true);
      setTimeout(() => setStatus(''), 3000);
    } catch (error) {
      console.error('Failed to save homepage content', error);
      setStatus('Unable to update homepage sections.');
      setStatusTone('danger');
      setTimeout(() => setStatus(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Homepage Sections</h1>
        <p className="page-subtitle">Edit the public homepage content and upload the images used across each section.</p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleImageFileChange}
      />

      {status && (
        <div className={`badge badge-${statusTone}`} style={{ marginBottom: '16px' }}>
          {uploadingKey ? `${status} (${uploadProgress}%)` : status}
        </div>
      )}

      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <h3 style={{ fontSize: '1.125rem' }}>Hero</h3>
        </div>
        <div className="card-body" style={{ display: 'grid', gap: '12px' }}>
          <input className="form-input" placeholder="Hero eyebrow" value={homepageContent.hero.eyebrow} onChange={(e) => updateField('hero', 'eyebrow', e.target.value)} />
          <input className="form-input" placeholder="Hero title" value={homepageContent.hero.title} onChange={(e) => updateField('hero', 'title', e.target.value)} />
          <textarea className="form-input form-textarea" placeholder="Hero subtitle" value={homepageContent.hero.subtitle} onChange={(e) => updateField('hero', 'subtitle', e.target.value)} />
          <div style={{ fontWeight: 700, marginTop: '8px' }}>Hero Slide Images</div>
          {imageTargets.heroSlides.map((slide, index) => (
            <div key={`hero-slide-${index}`} style={{ display: 'grid', gap: '8px', padding: '12px', borderRadius: '12px', background: 'var(--gray-50)', border: '1px solid var(--gray-100)' }}>
              <input className="form-input" placeholder="Image URL" value={slide.src} onChange={(e) => updateHeroSlide(index, 'src', e.target.value)} />
              <input className="form-input" placeholder="Link path" value={slide.link || ''} onChange={(e) => updateHeroSlide(index, 'link', e.target.value)} />
              <button type="button" className="btn btn-outline btn-sm" onClick={() => startImageUpload({ kind: 'heroSlide', index, key: `hero-${index}` })} disabled={Boolean(uploadingKey) || isSaving}>
                {uploadingKey === `hero-${index}` ? `Uploading… ${uploadProgress}%` : 'Upload slide image'}
              </button>
              {slide.src && (
                <img src={slide.src} alt={`Hero slide ${index + 1}`} style={{ width: '100%', maxWidth: '320px', borderRadius: '12px', border: '1px solid var(--gray-100)' }} />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <h3 style={{ fontSize: '1.125rem' }}>Courses Section</h3>
        </div>
        <div className="card-body" style={{ display: 'grid', gap: '12px' }}>
          <input className="form-input" placeholder="Courses heading" value={homepageContent.courses.heading} onChange={(e) => updateField('courses', 'heading', e.target.value)} />
          <input className="form-input" placeholder="Courses button label" value={homepageContent.courses.buttonLabel} onChange={(e) => updateField('courses', 'buttonLabel', e.target.value)} />
          {imageTargets.courseCards.map((card, index) => (
            <div key={`course-card-${index}`} style={{ display: 'grid', gap: '8px', padding: '12px', borderRadius: '12px', background: 'var(--gray-50)', border: '1px solid var(--gray-100)' }}>
              <div style={{ fontSize: '0.8125rem', fontWeight: 600 }}>Course Card {index + 1}</div>
              <input className="form-input" placeholder="Title" value={card.title} onChange={(e) => updateCard('courses', index, 'title', e.target.value)} />
              <textarea className="form-input form-textarea" placeholder="Description" value={card.description} onChange={(e) => updateCard('courses', index, 'description', e.target.value)} />
              <input className="form-input" placeholder="Image URL" value={card.image || ''} onChange={(e) => updateCard('courses', index, 'image', e.target.value)} />
              <input className="form-input" placeholder="Link path" value={card.link || ''} onChange={(e) => updateCard('courses', index, 'link', e.target.value)} />
              <button type="button" className="btn btn-outline btn-sm" onClick={() => startImageUpload({ kind: 'courseCard', index, key: `course-${index}` })} disabled={Boolean(uploadingKey) || isSaving}>
                {uploadingKey === `course-${index}` ? `Uploading… ${uploadProgress}%` : 'Upload course image'}
              </button>
              {card.image && (
                <img src={card.image} alt={card.title} style={{ width: '100%', maxWidth: '220px', borderRadius: '12px', border: '1px solid var(--gray-100)' }} />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <h3 style={{ fontSize: '1.125rem' }}>Video Section</h3>
        </div>
        <div className="card-body" style={{ display: 'grid', gap: '12px' }}>
          <input className="form-input" placeholder="Video heading" value={homepageContent.video.heading} onChange={(e) => updateField('video', 'heading', e.target.value)} />
          <input className="form-input" placeholder="YouTube URL" value={homepageContent.video.url} onChange={(e) => updateField('video', 'url', e.target.value)} />
        </div>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <h3 style={{ fontSize: '1.125rem' }}>Why Choose Section</h3>
        </div>
        <div className="card-body" style={{ display: 'grid', gap: '12px' }}>
          <input className="form-input" placeholder="Heading" value={homepageContent.whyChoose.heading} onChange={(e) => updateField('whyChoose', 'heading', e.target.value)} />
          {homepageContent.whyChoose.cards.map((card, index) => (
            <div key={`why-card-${index}`} style={{ display: 'grid', gap: '8px', padding: '12px', borderRadius: '12px', background: 'var(--gray-50)', border: '1px solid var(--gray-100)' }}>
              <input className="form-input" placeholder="Title" value={card.title} onChange={(e) => updateCard('whyChoose', index, 'title', e.target.value)} />
              <textarea className="form-input form-textarea" placeholder="Description" value={card.description} onChange={(e) => updateCard('whyChoose', index, 'description', e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <h3 style={{ fontSize: '1.125rem' }}>Events Section</h3>
        </div>
        <div className="card-body" style={{ display: 'grid', gap: '12px' }}>
          <input className="form-input" placeholder="Heading" value={homepageContent.events.heading} onChange={(e) => updateField('events', 'heading', e.target.value)} />
          <textarea className="form-input form-textarea" placeholder="Section copy" value={homepageContent.events.copy} onChange={(e) => updateField('events', 'copy', e.target.value)} />
          <input className="form-input" placeholder="Button label" value={homepageContent.events.buttonLabel} onChange={(e) => updateField('events', 'buttonLabel', e.target.value)} />
        </div>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <h3 style={{ fontSize: '1.125rem' }}>Footer CTA</h3>
        </div>
        <div className="card-body" style={{ display: 'grid', gap: '12px' }}>
          <input className="form-input" placeholder="CTA title" value={homepageContent.cta.title} onChange={(e) => updateField('cta', 'title', e.target.value)} />
          <textarea className="form-input form-textarea" placeholder="CTA subtitle" value={homepageContent.cta.subtitle} onChange={(e) => updateField('cta', 'subtitle', e.target.value)} />
        </div>
      </div>

      <button className="btn btn-primary" onClick={handleSave} disabled={Boolean(uploadingKey) || isSaving}>
        {isSaving ? 'Saving…' : 'Save Homepage Sections'}
      </button>

      {showSavedModal && (
        <div className="modal-overlay" onClick={() => setShowSavedModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Homepage updated</h3>
              <button onClick={() => setShowSavedModal(false)} className="btn btn-ghost btn-icon">
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.9375rem', color: 'var(--gray-600)', marginBottom: '8px' }}>
                Your homepage section changes have been saved successfully.
              </p>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                Uploaded images and text edits are now ready for the public homepage.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setShowSavedModal(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
